import { useCallback, useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';
import { StickyContainer } from 'react-sticky';

import { ISelectOption } from 'UIPackage/MainModule/components/SelectInput';
import { HeaderInfo } from 'CorePackage/HeaderInfoModule/components/HeaderInfo';
import { LawInterfacesEnum } from 'CorePackage/MainModule/enums/LawInterfacesEnum';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { getLawInterface } from 'CorePackage/MainModule/reducers/coreReducer';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { KnmVersionsEnum } from 'APIPackage/KnmModule/enums/KnmVersionsEnum';
import { Table } from 'CorePackage/TableModule/components/Table';
import { getStorageItem, setStorageItem } from 'CorePackage/MainModule/common/localStore';
import { StorageNamesEnum } from 'CorePackage/MainModule/enums/StorageNamesEnum';
import { getSortOrder } from 'CorePackage/MainModule/common/getSortOrder';
import { handleSortChange } from 'CorePackage/MainModule/common/handleSortChange';
import { ISimilarity } from 'APIPackage/SimilarityModule/interfaces/ISimilarity';
import { ITableColumn } from 'CorePackage/TableModule/interfaces/ITableColumn';
import { IInitialOrder } from 'CorePackage/MainModule/interfaces/IInitialOrder';
import { TableColumnSortEnum } from 'CorePackage/MainModule/enums/TableColumnSortEnum';
import { ISimilarityStatusEnum } from 'APIPackage/SimilarityModule/interfaces/ISimilarityStatus';
import { formatDate } from 'CorePackage/MainModule/common/formatDate';
import { DATE_FORMAT_SHORT_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { IconMenu } from 'UIPackage/IconsModule/components/IconMenu';
import { Dropdown } from 'UIPackage/MainModule/components/Dropdown';
import { DropdownItem } from 'UIPackage/MainModule/components/DropdownItem';
import { IDropdownItem } from 'UIPackage/MainModule/interfaces/IDropdownItem';
import { ISimilaritiesFilter } from 'APIPackage/SimilarityModule/interfaces/ISimilaritiesFilter';
import { getUserRoleIsSupervisor } from 'AuthPackage/MainModule/selectors/authSelectors';
import { dictionariesKnoOrganizationsItemGet } from 'CorePackage/DictionariesModule/actions/dictionariesActions';

import { SimilaritiesDateModal } from './SimilaritiesDateModal';
import { SimilaritiesFiltersModal } from './SimilaritiesFiltersModal';
import { SimilaritiesPanel } from './SimilaritiesPanel';
import styles from './Similarities.module.scss';
import {
  similaritiesChangeStatus,
  similaritiesGetData,
  similaritiesGetDataNextPage,
  similaritiesInitPage,
  similaritiesSetOrder,
} from '../actions/similaritiesActions';
import { similaritiesTableHeaders } from '../constants/similaritiesTableHeaders';
import columnStyles from './SimilaritiesTable.module.scss';
import {
  getSimilaritiesData,
  getSimilaritiesFilter,
  getSimilaritiesTotalCount,
} from '../selectors/similaritiesSelectors';

interface ISimilaritiesProps {
  lawInterface: LawInterfacesEnum;
  totalCount: number;
  data: ISimilarity[];
  filter: ISimilaritiesFilter;
  isSupervisor: boolean;
}

const SimilaritiesWithoutStore = ({ lawInterface, totalCount, data, filter, isSupervisor }: ISimilaritiesProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const { ERP, ERKNM } = KnmVersionsEnum;
  const version: KnmVersionsEnum = lawInterface === LawInterfacesEnum.FZ_248 ? ERKNM : ERP;

  const [isFiltersModalVisible, setIsFiltersModalVisible] = useState(false);
  const [isSimilaritiesDateModalVisible, setIsSimilaritiesDateModalVisible] = useState(false);
  const [startDateOptions, setStartDateOptions] = useState<ISelectOption[]>([]);
  const [similarityId, setSimilarityId] = useState('');

  const onFilterModalClose = useCallback(() => setIsFiltersModalVisible(false), []);
  const onDateModalClose = useCallback(() => setIsSimilaritiesDateModalVisible(false), []);

  function getInitialTableColumns(knmListOrder?: string) {
    const initialOrderString = knmListOrder;
    const initialOrder: IInitialOrder[] =
      initialOrderString?.split(';').map((orderStringItem: string) => {
        const [id, value] = orderStringItem.split(',');

        return {
          id,
          value,
        } as IInitialOrder;
      }) || [];

    return similaritiesTableHeaders.map(initialTableColumn => ({
      ...initialTableColumn,
      sort: initialOrderString
        ? initialOrder.find(orderItem => orderItem.id === initialTableColumn.name)?.value || TableColumnSortEnum.NONE
        : initialTableColumn.sort,
    }));
  }

  const renderDropdown = (row: ISimilarity) => {
    const { id, datesToJoin } = row;

    function onApproveClick() {
      dispatch(similaritiesChangeStatus(ISimilarityStatusEnum.RESOLVED, id, '', filter));
    }

    function onMergeClick() {
      setStartDateOptions(
        datesToJoin.map(date => ({
          id: date,
          value: date,
          label: formatDate({ date, dateFormat: DATE_FORMAT_SHORT_FNS }),
        })),
      );
      setSimilarityId(id);
      setIsSimilaritiesDateModalVisible(true);
    }

    const dropdownItems: IDropdownItem[] = [
      {
        id: 'approve',
        content: <DropdownItem onClick={onApproveClick}>Принудительно разрешить</DropdownItem>,
      },
      {
        id: 'merge',
        content: <DropdownItem onClick={onMergeClick}>Совместить</DropdownItem>,
      },
    ];

    return (
      <Dropdown items={dropdownItems}>
        <button className={styles.MenuButton} type="button">
          <IconMenu className={styles.MenuButtonIcon} />
        </button>
      </Dropdown>
    );
  };

  const similaritiesListOrder = getStorageItem<string>(StorageNamesEnum.SIMILARITIES_LIST_ORDER);
  const [tableColumns, setTableColumns] = useState<ITableColumn<ISimilarity>[]>(
    getInitialTableColumns(similaritiesListOrder),
  );

  useEffect(() => {
    dispatch(similaritiesInitPage());
    dispatch(dictionariesKnoOrganizationsItemGet());
  }, [dispatch]);

  const order = getSortOrder(tableColumns);

  useEffect(() => {
    setStorageItem(StorageNamesEnum.SIMILARITIES_LIST_ORDER, order);
    dispatch(similaritiesSetOrder(order));
  }, [dispatch, order]);

  const handleSortClick = useCallback(
    (id: keyof ISimilarity) => {
      const newColumnsData = handleSortChange(tableColumns, id);

      setTableColumns(newColumnsData);
      const newOrder = getSortOrder<ISimilarity>(newColumnsData);

      setStorageItem(StorageNamesEnum.SIMILARITIES_LIST_ORDER, newOrder);
      dispatch(similaritiesSetOrder(newOrder));
      dispatch(similaritiesGetData());
    },
    [dispatch, tableColumns],
  );

  const handleLoadMore = useCallback(() => dispatch(similaritiesGetDataNextPage()), [dispatch]);

  return (
    <>
      <HeaderInfo />
      <div className={styles.SimilaritiesPage}>
        <SimilaritiesPanel setFiltersModalVisible={setIsFiltersModalVisible} />
        <StickyContainer>
          <Table<ISimilarity>
            columns={tableColumns}
            data={data}
            onSortChange={handleSortClick}
            columnStyles={columnStyles}
            handleLoadClick={handleLoadMore}
            totalCount={totalCount}
            headPlacement="centre"
            renderDropdown={isSupervisor ? undefined : renderDropdown}
          />
        </StickyContainer>
      </div>
      <SimilaritiesFiltersModal isVisible={isFiltersModalVisible} onClose={onFilterModalClose} version={version} />
      <SimilaritiesDateModal
        isVisible={isSimilaritiesDateModalVisible}
        onClose={onDateModalClose}
        startDateOptions={startDateOptions}
        similarityId={similarityId}
      />
    </>
  );
};

const mapStateToProps = (state: IRootState) => ({
  lawInterface: getLawInterface(state),
  totalCount: getSimilaritiesTotalCount(state),
  data: getSimilaritiesData(state),
  filter: getSimilaritiesFilter(state),
  isSupervisor: getUserRoleIsSupervisor(state),
});

export const Similarities = connect(mapStateToProps)(SimilaritiesWithoutStore);
